import type {GeneratedSuggestion} from '@github-ui/copilot-chat-types'
import {updatedThreadSuggestions} from '@github-ui/copilot-chat-helpers/prompts'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {CopilotIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import styles from './AhpSessionComposer.module.css'

interface EmptyStateProps {
  /** Reference type key for selecting contextual suggestions (e.g. 'repository', 'file'). */
  suggestionContext?: string
  onSelectSuggestion: (suggestion: GeneratedSuggestion) => Promise<void>
}

const MAX_SUGGESTIONS = 3

export function suggestionContextForAttachment(attachment: MessageAttachment | undefined): string {
  if (!attachment) return 'default'
  if (attachment.displayKind === 'repository') return 'repository'

  const detail = attachment._meta?.['copilot.attachmentDetail'] as {type?: string; referenceType?: string} | undefined
  if (detail?.type === 'github_reference') {
    if (detail.referenceType === 'pr') return 'pull-request'
    if (detail.referenceType === 'issue' || detail.referenceType === 'discussion') return detail.referenceType
  }
  if (detail?.type === 'github_file_diff') return 'file-diff'
  if (detail?.type === 'github_snippet') return 'snippet'
  if (detail?.type === 'github_file' || attachment.displayKind === 'document') return 'file'
  return 'default'
}

/**
 * Empty-state view shown in the embedded chat panel when no conversation has
 * started. Renders the Copilot icon, contextual suggestion pills, and a
 * disclaimer line. Always uses `updatedThreadSuggestions` regardless of
 * feature flags, selecting the bucket matching `suggestionContext`.
 */
export function EmptyState({suggestionContext = 'default', onSelectSuggestion}: EmptyStateProps) {
  const shownReferenceGroupRef = useRef<string | undefined>(undefined)
  const suggestions = useMemo(() => {
    const bucket = updatedThreadSuggestions[suggestionContext] ?? updatedThreadSuggestions.default ?? []
    return bucket.slice(0, MAX_SUGGESTIONS)
  }, [suggestionContext])

  useEffect(() => {
    if (suggestions.length === 0 || shownReferenceGroupRef.current === suggestionContext) return
    shownReferenceGroupRef.current = suggestionContext
    sendEvent('copilot_generated_suggestions_shown', {
      referenceGroup: suggestionContext,
    })
  }, [suggestionContext, suggestions])

  const handleSelect = useCallback(
    (suggestion: GeneratedSuggestion) => {
      sendEvent('copilot_generated_suggestion_click', {
        intent: suggestion.intent,
        mode: suggestion.mode,
        content: suggestion.question,
        referenceGroup: suggestionContext,
      })
      void onSelectSuggestion(suggestion)
    },
    [onSelectSuggestion, suggestionContext],
  )

  return (
    <div className={styles.EmptyState}>
      <div className={styles.EmptyStateIcon}>
        <CopilotIcon size={32} />
      </div>
      {suggestions.length > 0 && (
        <ul className={styles.EmptyStateSuggestions} aria-label="Suggested prompts">
          {suggestions.map(s => (
            <li key={s.question}>
              <Button type="button" className={styles.SuggestionPill} onClick={() => handleSelect(s)}>
                {s.title ?? s.question}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className={styles.EmptyStateDisclaimer}>Copilot can make mistakes. Check important info.</p>
    </div>
  )
}
